import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, ArrowLeft, Users, Scale, AlertTriangle, CheckCircle, 
  ChevronDown, ChevronRight, Lightbulb, Target, MessageSquare,
  Copy, BookOpen, Loader2, Info, Sparkles, ThumbsUp, ThumbsDown,
  AlertCircle, FileText, Award
} from 'lucide-react';

const ROLE_LABELS = {
  arrendador: { label: "Arrendador", icon: "🏠", opposite: "arrendatario" },
  arrendatario: { label: "Arrendatario", icon: "🔑", opposite: "arrendador" },
  vendedor: { label: "Vendedor", icon: "💰", opposite: "comprador" },
  comprador: { label: "Comprador", icon: "🛒", opposite: "vendedor" },
  empleador: { label: "Empleador", icon: "🏢", opposite: "trabajador" },
  trabajador: { label: "Trabajador", icon: "👤", opposite: "empleador" }
};

const CONTRACT_TYPES = [
  { id: "arriendo", name: "Arriendo", roles: ["arrendador", "arrendatario"] },
  { id: "compraventa", name: "Compraventa", roles: ["vendedor", "comprador"] },
  { id: "laboral", name: "Laboral", roles: ["empleador", "trabajador"] }
];

const NegotiationAssistantPage = () => {
  const { api } = useAuth();
  const [searchParams] = useSearchParams();

  const [contractType, setContractType] = useState(searchParams.get('type') || '');
  const [yourRole, setYourRole] = useState(searchParams.get('role') || '');
  const [loading, setLoading] = useState(false);

  // Data from API
  const [strategies, setStrategies] = useState(null);
  const [counterpartyAnalysis, setCounterpartyAnalysis] = useState(null);
  const [talkingPoints, setTalkingPoints] = useState(null);

  // UI State
  const [activeTab, setActiveTab] = useState('counterparty');
  const [expandedClause, setExpandedClause] = useState(null);
  const [clauseDecisions, setClauseDecisions] = useState({});

  useEffect(() => {
    if (contractType && yourRole) {
      fetchNegotiationData();
    }
  }, [contractType, yourRole]);

  const fetchNegotiationData = async () => {
    setLoading(true);
    try {
      const [strategiesRes, counterpartyRes, talkingPointsRes] = await Promise.all([
        api.get(`/negotiation/strategies/${contractType}?role=${yourRole}`),
        api.get(`/negotiation/counterparty-analysis/${contractType}?your_role=${yourRole}`),
        api.get(`/negotiation/talking-points/${contractType}?your_role=${yourRole}`)
      ]);

      setStrategies(strategiesRes.data);
      setCounterpartyAnalysis(counterpartyRes.data);
      setTalkingPoints(talkingPointsRes.data);
    } catch (error) {
      console.error('Error fetching negotiation data:', error);
      toast.error('Error al cargar datos de negociación');
    } finally {
      setLoading(false);
    }
  };

  const handleClauseDecision = (clauseId, decision) => {
    setClauseDecisions(prev => ({
      ...prev,
      [clauseId]: decision
    }));
    toast.success(`Decisión registrada: ${decision === 'accept' ? 'Aceptar' : decision === 'negotiate' ? 'Negociar' : 'Rechazar'}`);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado al portapapeles');
  };

  const availableRoles = CONTRACT_TYPES.find(ct => ct.id === contractType)?.roles || [];

  return (
    <div className="min-h-screen mesh-gradient">
      {/* Header */}
      <header className="glass border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/tools" className="flex items-center space-x-3 text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Herramientas</span>
            </Link>
            <div className="flex items-center space-x-3">
              <Sparkles className="w-6 h-6 text-purple-500" />
              <span className="font-semibold font-['Outfit']">Asistente de Negociación</span>
            </div>
            <div />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Setup Section */}
        {(!contractType || !yourRole) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto"
          >
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold font-['Outfit'] mb-3">
                Prepara tu Negociación
              </h1>
              <p className="text-slate-400">
                Te mostraremos las cláusulas que típicamente solicita la contraparte
                y cómo responder a cada una.
              </p>
            </div>

            <div className="space-y-6">
              {/* Contract Type Selection */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-400" />
                  Tipo de Contrato
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {CONTRACT_TYPES.map((ct) => (
                    <button
                      key={ct.id}
                      onClick={() => { setContractType(ct.id); setYourRole(''); }}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        contractType === ct.id
                          ? 'border-blue-500 bg-blue-500/10'
                          : 'border-slate-700 hover:border-blue-500/50'
                      }`}
                      data-testid={`contract-type-${ct.id}`}
                    >
                      <span className="font-medium">{ct.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Role Selection */}
              {contractType && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-slate-800/50 border border-slate-700 rounded-xl p-6"
                >
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-purple-400" />
                    Tu Rol en el Contrato
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {availableRoles.map((role) => {
                      const roleInfo = ROLE_LABELS[role];
                      return (
                        <button
                          key={role}
                          onClick={() => setYourRole(role)}
                          className={`p-4 rounded-lg border-2 transition-all flex items-center gap-3 ${
                            yourRole === role
                              ? 'border-purple-500 bg-purple-500/10'
                              : 'border-slate-700 hover:border-purple-500/50'
                          }`}
                          data-testid={`role-${role}`}
                        >
                          <span className="text-2xl">{roleInfo?.icon}</span>
                          <span className="font-medium">{roleInfo?.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}

        {/* Main Content */}
        {contractType && yourRole && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {/* Context Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <span className="text-2xl">{ROLE_LABELS[yourRole]?.icon}</span>
                <div>
                  <h2 className="font-bold text-lg">
                    Negociando como {ROLE_LABELS[yourRole]?.label}
                  </h2>
                  <p className="text-sm text-slate-400">
                    Contraparte: {ROLE_LABELS[ROLE_LABELS[yourRole]?.opposite]?.label}
                  </p>
                </div>
              </div>
              <button
                onClick={() => { setContractType(''); setYourRole(''); }}
                className="text-sm text-slate-400 hover:text-white"
              >
                Cambiar configuración
              </button>
            </div>

            {loading ? (
              <div className="text-center py-20">
                <Loader2 className="w-10 h-10 animate-spin mx-auto text-purple-500 mb-4" />
                <p className="text-slate-400">Analizando posiciones de negociación...</p>
              </div>
            ) : (
              <>
                {/* Tabs */}
                <div className="flex gap-2 mb-6 border-b border-slate-700 pb-4">
                  {[
                    { id: 'counterparty', label: 'Cláusulas de Contraparte', icon: AlertTriangle },
                    { id: 'strategies', label: 'Estrategias', icon: Target },
                    { id: 'talking-points', label: 'Argumentos', icon: MessageSquare }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                        activeTab === tab.id
                          ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                          : 'text-slate-400 hover:text-white hover:bg-slate-800'
                      }`}
                    >
                      <tab.icon className="w-4 h-4" />
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* Tab Content */}
                <AnimatePresence mode="wait">
                  {/* Counterparty Clauses Tab */}
                  {activeTab === 'counterparty' && counterpartyAnalysis && (
                    <motion.div
                      key="counterparty"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="space-y-4"
                    >
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5" />
                          <div>
                            <h4 className="font-medium text-amber-300">Prepárate para estas solicitudes</h4>
                            <p className="text-sm text-amber-200/70">
                              La contraparte ({ROLE_LABELS[ROLE_LABELS[yourRole]?.opposite]?.label}) 
                              típicamente solicita las siguientes cláusulas. Decide cómo responderás a cada una.
                            </p>
                          </div>
                        </div>
                      </div>

                      {counterpartyAnalysis.their_typical_clauses?.map((clause) => {
                        const isExpanded = expandedClause === clause.id;
                        const decision = clauseDecisions[clause.id];

                        return (
                          <div
                            key={clause.id}
                            className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden"
                          >
                            <div
                              className="p-4 cursor-pointer"
                              onClick={() => setExpandedClause(isExpanded ? null : clause.id)}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                                      clause.risk_level === 'alto' ? 'bg-red-500/20 text-red-400' :
                                      clause.risk_level === 'medio-alto' ? 'bg-orange-500/20 text-orange-400' :
                                      'bg-yellow-500/20 text-yellow-400'
                                    }`}>
                                      Riesgo: {clause.risk_level}
                                    </span>
                                    <span className="text-xs text-slate-500">{clause.type}</span>
                                    {decision && (
                                      <span className={`text-xs px-2 py-0.5 rounded ${
                                        decision === 'accept' ? 'bg-green-500/20 text-green-400' :
                                        decision === 'negotiate' ? 'bg-blue-500/20 text-blue-400' :
                                        'bg-red-500/20 text-red-400'
                                      }`}>
                                        {decision === 'accept' ? 'Aceptar' : decision === 'negotiate' ? 'Negociar' : 'Rechazar'}
                                      </span>
                                    )}
                                  </div>
                                  <h4 className="font-semibold">{clause.name}</h4>
                                  <p className="text-sm text-slate-400 mt-1">{clause.likely_reason}</p>
                                </div>
                                <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                              </div>
                            </div>

                            <AnimatePresence>
                              {isExpanded && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: 'auto', opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  className="border-t border-slate-700"
                                >
                                  <div className="p-4 space-y-4">
                                    {/* Clause Content */}
                                    <div>
                                      <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-medium text-slate-400">Texto típico:</span>
                                        <button
                                          onClick={() => copyToClipboard(clause.content)}
                                          className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                                        >
                                          <Copy className="w-3 h-3" /> Copiar
                                        </button>
                                      </div>
                                      <div className="bg-slate-900 rounded-lg p-3 text-sm text-slate-300 font-mono max-h-32 overflow-y-auto">
                                        {clause.content}
                                      </div>
                                    </div>

                                    {/* Response Options */}
                                    <div>
                                      <h5 className="text-sm font-medium mb-3">Opciones de respuesta:</h5>
                                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                        {clause.response_options?.map((option, i) => (
                                          <div key={i} className="p-3 bg-slate-900/50 rounded-lg">
                                            <p className="font-medium text-sm">{option.option}</p>
                                            <p className="text-xs text-slate-400 mt-1">{option.approach}</p>
                                          </div>
                                        ))}
                                      </div>
                                    </div>

                                    {/* Compromise Suggestions */}
                                    <div>
                                      <h5 className="text-sm font-medium mb-2">Posibles compromisos:</h5>
                                      <ul className="space-y-1">
                                        {clause.compromise_suggestions?.map((suggestion, i) => (
                                          <li key={i} className="text-sm text-slate-400 flex items-start gap-2">
                                            <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                                            {suggestion}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>

                                    {/* Decision Buttons */}
                                    <div className="flex gap-2 pt-2">
                                      <button
                                        onClick={() => handleClauseDecision(clause.id, 'accept')}
                                        className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                                          decision === 'accept'
                                            ? 'bg-green-500 text-white'
                                            : 'bg-green-500/10 text-green-400 hover:bg-green-500/20'
                                        }`}
                                      >
                                        <ThumbsUp className="w-4 h-4" /> Aceptar
                                      </button>
                                      <button
                                        onClick={() => handleClauseDecision(clause.id, 'negotiate')}
                                        className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                                          decision === 'negotiate'
                                            ? 'bg-blue-500 text-white'
                                            : 'bg-blue-500/10 text-blue-400 hover:bg-blue-500/20'
                                        }`}
                                      >
                                        <MessageSquare className="w-4 h-4" /> Negociar
                                      </button>
                                      <button
                                        onClick={() => handleClauseDecision(clause.id, 'reject')}
                                        className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                                          decision === 'reject'
                                            ? 'bg-red-500 text-white'
                                            : 'bg-red-500/10 text-red-400 hover:bg-red-500/20'
                                        }`}
                                      >
                                        <ThumbsDown className="w-4 h-4" /> Rechazar
                                      </button>
                                    </div>
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        );
                      })}
                    </motion.div>
                  )}

                  {/* Strategies Tab */}
                  {activeTab === 'strategies' && strategies && (
                    <motion.div
                      key="strategies"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="grid grid-cols-1 lg:grid-cols-2 gap-6"
                    >
                      {/* Your Priorities */}
                      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                        <h3 className="font-semibold mb-4 flex items-center gap-2">
                          <Target className="w-5 h-5 text-green-400" />
                          Tus Prioridades
                        </h3>
                        <ul className="space-y-2">
                          {strategies.strategy?.priorities?.map((priority, i) => (
                            <li key={i} className="flex items-start gap-2 text-slate-300">
                              <span className="w-5 h-5 rounded-full bg-green-500/20 text-green-400 text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                                {i + 1}
                              </span>
                              {priority}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Flexible Points */}
                      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                        <h3 className="font-semibold mb-4 flex items-center gap-2">
                          <Scale className="w-5 h-5 text-blue-400" />
                          Puntos Flexibles
                        </h3>
                        <ul className="space-y-2">
                          {strategies.strategy?.flexible_points?.map((point, i) => (
                            <li key={i} className="flex items-start gap-2 text-slate-300">
                              <CheckCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                              {point}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Red Flags */}
                      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
                        <h3 className="font-semibold mb-4 flex items-center gap-2 text-red-300">
                          <AlertTriangle className="w-5 h-5" />
                          Señales de Alerta
                        </h3>
                        <ul className="space-y-2">
                          {strategies.strategy?.red_flags?.map((flag, i) => (
                            <li key={i} className="flex items-start gap-2 text-red-200/80">
                              <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                              {flag}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Leverage Points */}
                      <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-6">
                        <h3 className="font-semibold mb-4 flex items-center gap-2 text-purple-300">
                          <Award className="w-5 h-5" />
                          Puntos de Ventaja
                        </h3>
                        <ul className="space-y-2">
                          {strategies.strategy?.leverage_points?.map((point, i) => (
                            <li key={i} className="flex items-start gap-2 text-purple-200/80">
                              <Sparkles className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                              {point}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Tactics */}
                      <div className="lg:col-span-2 bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                        <h3 className="font-semibold mb-4 flex items-center gap-2">
                          <Lightbulb className="w-5 h-5 text-yellow-400" />
                          Tácticas de Negociación
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {strategies.tactics?.map((tactic, i) => (
                            <div key={i} className="p-4 bg-slate-900/50 rounded-lg">
                              <h4 className="font-medium text-yellow-300">{tactic.name}</h4>
                              <p className="text-sm text-slate-400 mt-1">{tactic.description}</p>
                              <p className="text-xs text-slate-500 mt-2">
                                <strong>Cuándo usar:</strong> {tactic.when_to_use}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Common Mistakes */}
                      <div className="lg:col-span-2 bg-amber-500/10 border border-amber-500/30 rounded-xl p-6">
                        <h3 className="font-semibold mb-4 flex items-center gap-2 text-amber-300">
                          <AlertTriangle className="w-5 h-5" />
                          Errores Comunes a Evitar
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {strategies.common_mistakes?.map((mistake, i) => (
                            <div key={i} className="flex items-start gap-2 text-amber-200/80">
                              <span className="text-amber-400">✗</span>
                              {mistake}
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Talking Points Tab */}
                  {activeTab === 'talking-points' && talkingPoints && (
                    <motion.div
                      key="talking-points"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="space-y-6"
                    >
                      {/* Opening Statement */}
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
                        <h3 className="font-semibold mb-3 flex items-center gap-2 text-blue-300">
                          <MessageSquare className="w-5 h-5" />
                          Declaración de Apertura
                        </h3>
                        <div className="relative">
                          <p className="text-blue-200/90 italic">"{talkingPoints.opening_statement}"</p>
                          <button
                            onClick={() => copyToClipboard(talkingPoints.opening_statement)}
                            className="absolute top-0 right-0 text-blue-400 hover:text-blue-300"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Talking Points */}
                      <div className="space-y-4">
                        <h3 className="font-semibold flex items-center gap-2">
                          <BookOpen className="w-5 h-5 text-green-400" />
                          Argumentos Preparados
                        </h3>
                        {talkingPoints.talking_points?.map((point, i) => (
                          <div key={i} className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
                            <div className="flex items-start justify-between mb-3">
                              <h4 className="font-semibold text-green-300">{point.topic}</h4>
                              <button
                                onClick={() => copyToClipboard(point.negotiation_script)}
                                className="text-slate-400 hover:text-white"
                              >
                                <Copy className="w-4 h-4" />
                              </button>
                            </div>
                            
                            <div className="space-y-3">
                              <div>
                                <span className="text-xs text-slate-500 uppercase">Tu posición:</span>
                                <p className="text-slate-300">{point.your_position}</p>
                              </div>
                              
                              <div>
                                <span className="text-xs text-slate-500 uppercase">Justificación:</span>
                                <p className="text-slate-400 text-sm">{point.justification}</p>
                              </div>
                              
                              <div className="bg-slate-900/50 rounded-lg p-3">
                                <span className="text-xs text-slate-500 uppercase">Script sugerido:</span>
                                <p className="text-slate-300 text-sm italic mt-1">{point.negotiation_script}</p>
                              </div>
                              
                              {point.legal_backing && (
                                <div className="flex items-center gap-2 text-xs text-slate-500">
                                  <Scale className="w-3 h-3" />
                                  Base legal: {point.legal_backing}
                                </div>
                              )}
                              
                              <div className="pt-2 border-t border-slate-700">
                                <span className="text-xs text-amber-400">Posición de respaldo:</span>
                                <p className="text-sm text-slate-400">{point.fallback_position}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Closing Checklist */}
                      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                        <h3 className="font-semibold mb-4 flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-emerald-400" />
                          Checklist de Cierre
                        </h3>
                        <ul className="space-y-2">
                          {talkingPoints.closing_checklist?.map((item, i) => (
                            <li key={i} className="flex items-center gap-3 text-slate-300">
                              <input 
                                type="checkbox" 
                                className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-emerald-500 focus:ring-emerald-500"
                              />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </>
            )}
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default NegotiationAssistantPage;
